import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { rtuStatusHistoryService } from '../../services/rtuStatusHistoryService';
import { RtuStatusHistoryPoint, RtuStatusHistoryResponse } from '../../types/RtuStatusHistory';
import { RtuSupportedRange } from '../../types/RtuStatus';
import { getStatusColor } from '../../utils/statusConfig';
import { useMetricCardCompact } from '../../hooks/useMetricCardCompact';
import { usePrimeChartPanelResize } from '../../hooks/usePrimeChartPanelResize';
import { RTU } from '../../types/RTU';
import { dashboardInsightsService } from '../../services/dashboardInsightsService';
import { DashboardReachabilitySummaryDto, DashboardRtuRowDto } from '../../types/DashboardInsights';
import { humanizeStatusReason, humanizeStatusSource } from '../../utils/statusDisplayConfig';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import { formatProducerName } from '../../utils/rtuDefaults';
import { SkeletonChartPanel, SkeletonRegion } from '../common/Skeleton/Skeleton';

const RANGE_PRESETS: Array<{ range: RtuSupportedRange; chipLabel: string }> = [
  { range: '24h', chipLabel: '24H' },
  { range: '7d', chipLabel: '7D' },
  { range: '30d', chipLabel: '30D' },
];

const formatBucketLabel = (bucketStartUtc: string, range: RtuSupportedRange) => {
  const parsed = new Date(bucketStartUtc);
  if (Number.isNaN(parsed.getTime())) {
    return bucketStartUtc;
  }

  if (range === '24h') {
    return parsed.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return parsed.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
};

const formatUtc = (value: string | null | undefined) => {
  if (!value) {
    return '\u2014';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const toPercent = (value: number, total: number) => {
  if (total <= 0) {
    return 0;
  }
  return Math.round((value / total) * 1000) / 10;
};

/** Backend may return buckets in any order; chart + summary rail must agree on chronology. */
const sortHistoryPointsAsc = (points: readonly RtuStatusHistoryPoint[]): RtuStatusHistoryPoint[] =>
  [...points].sort((a, b) => {
    const ta = new Date(a.bucketStartUtc).getTime();
    const tb = new Date(b.bucketStartUtc).getTime();
    const na = Number.isNaN(ta) ? 0 : ta;
    const nb = Number.isNaN(tb) ? 0 : tb;
    return na - nb;
  });

const hasStatusCounts = (point: RtuStatusHistoryPoint) =>
  point.onlineCount + point.offlineCount + (point.unstableCount ?? 0) + point.unknownCount > 0;

const latestPointWithCounts = (points: readonly RtuStatusHistoryPoint[]) =>
  [...points].reverse().find(hasStatusCounts);

type SummaryStatusKey = 'online' | 'offline' | 'unstable';

interface StatusHistoryChartProps {
  rtus?: RTU[];
}

type StatusDetailRow = DashboardRtuRowDto | RTU;

const isRtuDetailRow = (row: StatusDetailRow): row is RTU => 'publicIPAddress' in row;

const getStatusKeyFromValue = (value: unknown): SummaryStatusKey => {
  if (value === true) {
    return 'online';
  }
  if (value === false) {
    return 'offline';
  }

  const status = String(value ?? '').trim().toUpperCase();
  if (status === 'ONLINE' || status === 'ONLINE / REACHABLE' || status === 'REACHABLE') {
    return 'online';
  }
  if (status === 'UNSTABLE' || status === 'DEGRADED' || status === 'PARTIAL') {
    return 'unstable';
  }
  return 'offline';
};

const getRtuStatusKey = (rtu: RTU): SummaryStatusKey =>
  getStatusKeyFromValue(rtu.canonicalStatus || rtu.status || rtu.onlineStatus);

const getDashboardRowStatusKey = (row: DashboardRtuRowDto): SummaryStatusKey =>
  getStatusKeyFromValue(row.status);

const getStatusDetailName = (row: StatusDetailRow): string => row.name?.trim() || `RTU ${row.id}`;

const getStatusDetailProducer = (row: StatusDetailRow): string =>
  formatProducerName(isRtuDetailRow(row) ? row.provider : row.producer);

const getStatusDetailTarget = (row: StatusDetailRow): string =>
  (isRtuDetailRow(row) ? row.publicIPAddress : row.targetIp) || '-';

const getStatusDetailReason = (row: StatusDetailRow): string =>
  ('statusReason' in row && row.statusReason
    ? humanizeStatusReason(row.statusReason)
    : 'statusSource' in row && row.statusSource
      ? humanizeStatusSource(row.statusSource)
      : '-');

const getStatusDetailObserved = (row: StatusDetailRow): string | null | undefined =>
  isRtuDetailRow(row)
    ? row.statusObservedAtUtc || row.lastProbeAtUtc || row.lastData
    : row.lastProbeAtUtc || row.lastFileReceivedAtUtc;

export const StatusHistoryChart = ({ rtus = [] }: StatusHistoryChartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [range, setRange] = useState<RtuSupportedRange>('24h');
  const [history, setHistory] = useState<RtuStatusHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentReachability, setCurrentReachability] = useState<DashboardReachabilitySummaryDto | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<SummaryStatusKey | null>(null);
  const hasHistoryRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    hasHistoryRef.current = history !== null;
  }, [history]);

  const loadHistory = useCallback(
    async (selectedRange: RtuSupportedRange) => {
      const requestId = ++requestIdRef.current;
      if (hasHistoryRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await rtuStatusHistoryService.fetch({ range: selectedRange });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setHistory(response);
        setError(null);
      } catch (serviceError: any) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setError(
          serviceError?.response?.data?.message ||
            serviceError?.message ||
            t('dashboard.history.errors.loadFailed', {
              defaultValue: 'Unable to load fleet status history.',
            })
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    // `t` is only used for a failure-message string below, never to decide
    // what to fetch. Depending on it here recreated loadHistory (and
    // re-fired the effect below) on every i18next languageChanged event,
    // including spurious ones fired by an unrelated Redux dispatch - see
    // src/locales/i18n.ts. Translation stays current because `t` is still
    // read from closure; only the fetch-trigger identity is stabilized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    void loadHistory(range);
  }, [loadHistory, range]);

  useEffect(() => {
    let active = true;
    dashboardInsightsService
      .currentReachability()
      .then((response) => {
        if (active) {
          setCurrentReachability(response);
        }
      })
      .catch(() => {
        if (active) {
          setCurrentReachability(null);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const sortedPoints = useMemo(
    () => sortHistoryPointsAsc(history?.points ?? []),
    [history?.points]
  );

  const chartPoints = useMemo<RtuStatusHistoryPoint[]>(() => {
    if (sortedPoints.some(hasStatusCounts)) {
      return sortedPoints;
    }

    if (currentReachability && currentReachability.totalCount > 0) {
      return [
        {
          bucketStartUtc: currentReachability.generatedAtUtc,
          onlineCount: currentReachability.onlineCount,
          offlineCount: currentReachability.offlineCount,
          unstableCount: currentReachability.unstableCount,
          unknownCount: 0,
        },
      ];
    }

    return sortedPoints;
  }, [currentReachability, sortedPoints]);

  const chartType = 'bar' as const;

  const chartData = useMemo(() => {
    const points = chartPoints;
    const totals = points.map((point) =>
      Math.max(1, point.onlineCount + point.offlineCount + (point.unstableCount ?? 0) + point.unknownCount)
    );
    const offlineOrNotReachableCounts = points.map((point) => point.offlineCount + point.unknownCount);

    const buildDataset = (label: string, values: number[], color: string) => {
      const percentages = values.map((value, index) => toPercent(value, totals[index]));

      return {
        label,
        data: percentages,
        rawCounts: values,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        borderRadius: 3,
        barThickness: 12,
        stack: 'rtu-status',
      };
    };

    return {
      labels: points.map((point) => formatBucketLabel(point.bucketStartUtc, range)),
      datasets: [
        buildDataset(
          t('status.online'),
          points.map((point) => point.onlineCount),
          getStatusColor('online')
        ),
        buildDataset(
          t('status.offlineNotReachable', { defaultValue: 'Offline / Not Reachable' }),
          offlineOrNotReachableCounts,
          getStatusColor('offline')
        ),
        buildDataset(
          t('status.unstable', { defaultValue: 'Unstable' }),
          points.map((point) => point.unstableCount ?? 0),
          getStatusColor('unstable')
        ),
      ],
    };
  }, [chartPoints, range, t]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            color: '#20313f',
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          callbacks: {
            title: (tooltipItems: Array<{ dataIndex: number }>) => {
              const bucketStartUtc = chartPoints[tooltipItems[0]?.dataIndex]?.bucketStartUtc;
              return formatUtc(bucketStartUtc);
            },
            label: (context: any) => {
              const dataset = context.dataset as { label?: string; rawCounts?: number[] };
              const raw = dataset.rawCounts?.[context.dataIndex] ?? 0;
              const pct = Number(context.parsed?.y ?? 0).toFixed(1);
              return `${dataset.label ?? ''}: ${raw} (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: '#5d6f7e',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 9,
            font: {
              size: 11,
            },
          },
          grid: {
            color: 'rgba(32, 49, 63, 0.06)',
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          max: 100,
          ticks: {
            color: '#5d6f7e',
            stepSize: 20,
            callback: (value: number) => `${value}%`,
            font: {
              size: 11,
            },
          },
          grid: {
            color: 'rgba(32, 49, 63, 0.08)',
          },
        },
      },
    }),
    [chartPoints]
  );

  /** Sort for chart order, then pick newest bucket that already has rollup counts. */
  const summarySource = useMemo(() => {
    const points = sortedPoints;
    const summary = history?.summary ?? {
      latestOnlineCount: 0,
      latestOfflineCount: 0,
      latestUnstableCount: 0,
      latestUnknownCount: 0,
      transitionCount: 0,
    };
    const latestPoint = latestPointWithCounts(points);
    if (latestPoint) {
      return {
        latestOnlineCount: latestPoint.onlineCount,
        latestOfflineCount: latestPoint.offlineCount + latestPoint.unknownCount,
        latestUnstableCount: latestPoint.unstableCount ?? 0,
        latestUnknownCount: 0,
        transitionCount: summary.transitionCount,
      };
    }
    return {
      latestOnlineCount: summary.latestOnlineCount,
      latestOfflineCount: summary.latestOfflineCount + summary.latestUnknownCount,
      latestUnstableCount: summary.latestUnstableCount ?? 0,
      latestUnknownCount: 0,
      transitionCount: summary.transitionCount,
    };
  }, [sortedPoints, history?.summary]);

  const latestTotal =
    currentReachability?.totalCount ??
    summarySource.latestOnlineCount +
      summarySource.latestOfflineCount +
      summarySource.latestUnstableCount;

  const summaryRows = useMemo(
    () => [
      {
        key: 'online',
        label: t('status.online'),
        color: getStatusColor('online'),
        count: currentReachability?.onlineCount ?? summarySource.latestOnlineCount,
      },
      {
        key: 'offline',
        label: t('status.offlineNotReachable', { defaultValue: 'Offline / Not Reachable' }),
        color: getStatusColor('offline'),
        count: currentReachability?.offlineCount ?? summarySource.latestOfflineCount,
      },
      {
        key: 'unstable',
        label: t('status.unstable', { defaultValue: 'Unstable' }),
        color: getStatusColor('unstable'),
        count: currentReachability?.unstableCount ?? summarySource.latestUnstableCount,
      },
    ],
    [
      currentReachability?.offlineCount,
      currentReachability?.onlineCount,
      currentReachability?.unstableCount,
      summarySource.latestOfflineCount,
      summarySource.latestOnlineCount,
      summarySource.latestUnstableCount,
      t,
    ]
  );

  const selectedSummaryRow = useMemo(
    () => summaryRows.find((row) => row.key === selectedStatus) ?? null,
    [selectedStatus, summaryRows]
  );

  const selectedStatusRtus = useMemo(
    () => {
      if (!selectedStatus) {
        return [];
      }

      if (currentReachability?.devices?.length) {
        return currentReachability.devices.filter((row) => getDashboardRowStatusKey(row) === selectedStatus);
      }

      if (currentReachability && currentReachability.totalCount > 0) {
        return [];
      }

      return rtus.filter((rtu) => getRtuStatusKey(rtu) === selectedStatus);
    },
    [currentReachability, rtus, selectedStatus]
  );

  const selectedStatusEmptyMessage =
    selectedSummaryRow && selectedSummaryRow.count > 0 && currentReachability && selectedStatusRtus.length === 0
      ? t('dashboard.history.statusDetailsUnavailable', {
          defaultValue: 'RTU details for this status are unavailable. Refresh the dashboard and try again.',
        })
      : t('dashboard.history.noStatusRtus', {
          defaultValue: 'No RTUs in this status.',
        });

  const openRtuDetail = (row: StatusDetailRow) => {
    if (!row?.id) {
      return;
    }
    setSelectedStatus(null);
    navigate(`/rtupage/${row.id}`);
  };

  const { ref: metricBodyRef, compact: metricCompact } = useMetricCardCompact();
  const chartPanelRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart>(null);
  const historyLayoutKey = `${range}:${history?.generatedAtUtc ?? ''}:${currentReachability?.generatedAtUtc ?? ''}:${chartPoints.length}`;
  const showHistoryChart = Boolean(chartPoints.length > 0 && chartPoints.some(hasStatusCounts));
  usePrimeChartPanelResize(chartPanelRef, chartRef, showHistoryChart, historyLayoutKey);

  return (
    <div className="dashboard-history-chart">
      <div className="dashboard-history-chart__header">
        <div className="dashboard-history-chart__controls">
          <div className="dashboard-history-chart__chip-scroller">
            <div className="dashboard-history-chart__chip-group">
              {RANGE_PRESETS.map((preset) => (
                <button
                  key={preset.range}
                  type="button"
                  className={`dashboard-history-chart__chip${range === preset.range ? ' is-active' : ''}`}
                  onClick={() => setRange(preset.range)}
                >
                  {preset.chipLabel}
                </button>
              ))}
            </div>
          </div>
          <div className="dashboard-history-chart__meta">
            {refreshing ? <span>{t('dashboard.history.refreshing')}</span> : null}
            <span>{t('dashboard.history.updatedAt', { value: formatUtc(history?.generatedAtUtc) })}</span>
          </div>
        </div>
      </div>

      <div
        ref={metricBodyRef}
        className={`dashboard-history-chart__body${metricCompact ? ' dashboard-metric-card--compact' : ''}`}
      >
        <aside className="dashboard-history-chart__summary-rail" aria-label={t('dashboard.history.liveSummary', { defaultValue: 'Live summary' })}>
          <div className="dashboard-history-chart__summary-heading">
            {t('dashboard.history.currentStatus', { defaultValue: 'Current RTU Status' })}
          </div>
          {summaryRows.map((row) => {
            const percentage = toPercent(row.count, latestTotal);

            return (
              <button
                type="button"
                className="dashboard-history-chart__summary-row"
                key={row.key}
                onClick={() => setSelectedStatus(row.key as SummaryStatusKey)}
                aria-label={t('dashboard.history.showStatusDetails', {
                  defaultValue: 'Show {{status}} RTUs',
                  status: row.label,
                })}
              >
                <div className="dashboard-history-chart__summary-top">
                  <span>{row.label}</span>
                  <span>{percentage}%</span>
                </div>
                <div className="dashboard-history-chart__summary-bar">
                  <span style={{ width: `${percentage}%`, background: row.color }} />
                </div>
                <strong data-testid={`history-summary-${row.key}-count`}>
                  {row.count} / {latestTotal}
                </strong>
              </button>
            );
          })}
        </aside>

        <div ref={chartPanelRef} className="dashboard-history-chart__chart-panel">
          {loading && !history ? (
            <SkeletonRegion label={t('dashboard.history.loading', { defaultValue: 'Loading device status history...' })}>
              <SkeletonChartPanel />
            </SkeletonRegion>
          ) : null}

          {!loading && error && !history ? (
            <div className="dashboard-history-chart__state" role="alert">
              <div>{error}</div>
              <Button
                label={t('dashboard.history.retry')}
                className="p-button-sm p-button-outlined"
                onClick={() => void loadHistory(range)}
              />
            </div>
          ) : null}

          {showHistoryChart ? (
            <div className="dashboard-history-chart__canvas">
              <Chart ref={chartRef} type={chartType} data={chartData} options={chartOptions} />
            </div>
          ) : null}

          {history && !showHistoryChart && !loading ? (
            <div className="dashboard-history-chart__state" role="status">
              {t('dashboard.history.empty')}
            </div>
          ) : null}
        </div>
      </div>

      <Dialog
        header={selectedSummaryRow ? selectedSummaryRow.label : ''}
        visible={selectedStatus !== null}
        modal
        className="dashboard-status-detail-dialog"
        style={{ width: 'min(64rem, calc(100vw - 7rem))', maxWidth: '96vw' }}
        contentStyle={{ padding: '0.75rem 1rem 1rem' }}
        headerStyle={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e9ecef' }}
        onHide={() => setSelectedStatus(null)}
        blockScroll
      >
        <DataTable
          value={selectedStatusRtus}
          emptyMessage={selectedStatusEmptyMessage}
          scrollable
          scrollHeight="360px"
          showGridlines
          size="small"
          stripedRows
          rowClassName={() => 'dashboard-status-detail-dialog__row'}
          onRowClick={(event) => openRtuDetail(event.data as StatusDetailRow)}
          tableStyle={{ width: '100%', tableLayout: 'fixed' }}
        >
          <Column
            field="id"
            header={t('dashboard.tables.columns.wellId', { defaultValue: 'Well ID' })}
            style={{ width: '7rem' }}
            bodyStyle={{ textAlign: 'center' }}
          />
          <Column
            header={t('dashboard.tables.columns.rtuId', { defaultValue: 'RTU ID' })}
            body={(row: StatusDetailRow) => getStatusDetailName(row)}
            style={{ width: '18%' }}
          />
          <Column
            header={t('dashboard.tables.columns.producer', { defaultValue: 'Producer' })}
            body={(row: StatusDetailRow) => getStatusDetailProducer(row)}
            style={{ width: '18%' }}
          />
          <Column
            header={t('dashboard.tables.columns.publicIp', { defaultValue: 'Public IP' })}
            body={(row: StatusDetailRow) => getStatusDetailTarget(row)}
            style={{ width: '16%' }}
          />
          <Column
            header={t('dashboard.tables.columns.reason', { defaultValue: 'Reason' })}
            body={(row: StatusDetailRow) => getStatusDetailReason(row)}
            style={{ width: '22%' }}
          />
          <Column
            header={t('dashboard.tables.columns.observed', { defaultValue: 'Observed' })}
            body={(row: StatusDetailRow) => formatDisplayDateTime(getStatusDetailObserved(row), '-')}
            style={{ width: '20%' }}
          />
        </DataTable>
      </Dialog>
    </div>
  );
};

export default StatusHistoryChart;
